import { logger, FIELDS } from './logger.js';

/**
 * RoofFace Class
 * Encapsulates individual roof face masks with selection state and rendering logic
 */
export class RoofFace {
  constructor(maskArray, dimensions, index, solarPanels = null, fluxImage = null) {
    this.maskArray = maskArray;
    this.dimensions = dimensions; // [width, height]
    this.index = index;
    this.isSelected = true; // Default selected
    this.boundingBox = this.calculateBoundingBox();
    this.activePixelCount = this.countActivePixels();
    this.solarPanels = solarPanels; // Solar panel placement data
    this.fluxImage = fluxImage; // Annual flux visualization image
  }

  /**
   * Calculate tight bounding rectangle around the mask
   * @returns {Object} Bounding box with minX, maxX, minY, maxY
   */
  calculateBoundingBox() {
    let minX = this.dimensions[0], maxX = 0;
    let minY = this.dimensions[1], maxY = 0;

    for (let i = 0; i < this.maskArray.length; i++) {
      if (this.maskArray[i] > 0) {
        const x = i % this.dimensions[0];
        const y = Math.floor(i / this.dimensions[0]);
        minX = Math.min(minX, x);
        maxX = Math.max(maxX, x);
        minY = Math.min(minY, y);
        maxY = Math.max(maxY, y);
      }
    }

    return { minX, maxX, minY, maxY };
  }

  /**
   * Count active pixels for area calculation
   * @returns {number} Number of active pixels
   */
  countActivePixels() {
    return this.maskArray.filter(pixel => pixel > 0).length;
  }

  /**
   * Check if a point (in mask coordinates) hits this face
   * @param {number} x - X coordinate in mask space
   * @param {number} y - Y coordinate in mask space
   * @returns {boolean} True if point is within this face
   */
  containsPoint(x, y) {
    // Quick bounding box check first
    if (x < this.boundingBox.minX || x > this.boundingBox.maxX ||
        y < this.boundingBox.minY || y > this.boundingBox.maxY) {
      return false;
    }

    // Precise pixel-level check
    const pixelIndex = y * this.dimensions[0] + x;
    return this.maskArray[pixelIndex] > 0;
  }

  /**
   * Toggle selection state
   */
  toggle() {
    this.isSelected = !this.isSelected;
    logger.debug('Roof face toggled', {
      [FIELDS.ROOF_FACE_INDEX]: this.index,
      [FIELDS.IS_SELECTED]: this.isSelected
    });
  }

  /**
   * Render this face on the canvas
   * @param {CanvasRenderingContext2D} ctx - Canvas context
   * @param {number} scaleX - X scale factor
   * @param {number} scaleY - Y scale factor
   * @param {boolean} [isSelected] - Selection state (defaults to this.isSelected)
   * @returns {number} Number of pixels rendered
   */
  render(ctx, scaleX, scaleY, isSelected = this.isSelected) {
    // Only render solar panels, no mask overlay
    if (this.solarPanels && this.solarPanels.panels) {
      this.renderSolarPanels(ctx, scaleX, scaleY, isSelected);
    }

    logger.debug('Roof face rendered', {
      [FIELDS.ROOF_FACE_INDEX]: this.index,
      [FIELDS.IS_SELECTED]: isSelected
    });
    return 0; // No mask pixels rendered
  }

  /**
   * Render solar panels on top of the mask
   * @param {CanvasRenderingContext2D} ctx - Canvas context
   * @param {number} scaleX - X scale factor
   * @param {number} scaleY - Y scale factor
   * @param {boolean} [isSelected] - Selection state (defaults to this.isSelected)
   */
  renderSolarPanels(ctx, scaleX, scaleY, isSelected = this.isSelected) {
    if (!this.solarPanels || !this.solarPanels.panels || this.solarPanels.panels.length === 0) {
      return; // No panels to render
    }

    // Don't render panels if face is disabled
    if (!isSelected) {
      return;
    }

    // Render each panel based on face and panel enabled states
    this.solarPanels.panels.forEach(panel => {
      const x = panel.x * scaleX;
      const y = panel.y * scaleY;
      const w = panel.width * scaleX;
      const h = panel.height * scaleY;

      const panelEnabled = panel.enabled !== false; // Default true if not set

      // Simple two-state rendering logic
      if (panelEnabled) {
        // Enabled - Dark navy blue panels
        ctx.fillStyle = 'rgba(30, 64, 175, 0.9)'; // Dark navy blue
        ctx.fillRect(x, y, w, h);

        // White border for enabled panels
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.8)';
      } else {
        // Disabled - Gray panels
        ctx.fillStyle = 'rgba(100, 100, 100, 0.5)';
        ctx.fillRect(x, y, w, h);

        // Gray border for disabled panels
        ctx.strokeStyle = 'rgba(150, 150, 150, 0.6)';
      }

      ctx.lineWidth = 2;
      ctx.strokeRect(x, y, w, h);
    });

    logger.debug('Solar panels rendered', {
      [FIELDS.ROOF_FACE_INDEX]: this.index,
      panelCount: this.solarPanels.panels.length,
      enabledCount: this.solarPanels.panels.filter(p => p.enabled !== false).length,
      [FIELDS.IS_SELECTED]: isSelected
    });
  }

  /**
   * Get current state for external access
   * @returns {Object} State object with index, selection, pixel count, and bounding box
   */
  getState() {
    return {
      index: this.index,
      isSelected: this.isSelected,
      pixelCount: this.activePixelCount,
      boundingBox: this.boundingBox
    };
  }
}
